import CloseIcon from '@mui/icons-material/Close';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import {arrayOf, func, object, string} from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import {useController, useFormContext, useFormState} from 'react-hook-form';

const filter = createFilterOptions({ignoreCase: true, trim: true});

function appendIfMissing(options, candidate) {
  if (options.some(option => option.value === candidate.value)) {
    return options;
  }
  return [...options, candidate];
}

function removeIfPresent(options, candidate) {
  return options.filter(option => option.value !== candidate.value);
}

function OptionButton({option, onClick}) {
  return (
    <Button endIcon={<CloseIcon fontSize="small" />} onClick={onClick}>
      {option.value}
    </Button>
  );
}

OptionButton.propTypes = {
  option: object.isRequired,
  onClick: func.isRequired,
};

function getOptionLabel(option) {
  if (typeof option === 'string') {
    return option;
  }
  return option.label;
}

function MultiSelect({
  name,
  possibleValues = [],
  defaultValues = [],
  rules,
  label = 'Values',
  inputLabel = 'Add value',
}) {
  const {control} = useFormContext();
  const {field} = useController({
    name,
    rules,
    control,
    defaultValue: defaultValues,
  });
  const {errors} = useFormState({control, name});

  const [value, setValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(
    defaultValues.map(value => ({label: value, value})),
  );

  const update = useCallback(
    options => {
      setSelectedOptions(options);
      setValue('');
      field.onChange(options.map(option => option.value));
    },
    [field],
  );

  const errorMessage = errors[name] && errors[name].message;

  const selectedValues = selectedOptions.map(option => option.value);

  const unselectedOptions = useMemo(() => {
    const selected = new Set(selectedOptions.map(option => option.value));
    return possibleValues
      .filter(value => !selected.has(value))
      .map(value => ({label: value, value}));
  }, [selectedOptions, possibleValues]);

  const handleChange = useCallback(
    (event, option) => {
      if (event.type === 'blur') {
        setValue('');
        return;
      }
      update(appendIfMissing(selectedOptions, option));
    },
    [selectedOptions, update],
  );

  const handleInputChange = useCallback(
    (_, newValue) => setValue(newValue),
    [],
  );

  const filterOptions = useCallback(
    (options, params) => {
      const filtered = filter(options, params);
      const value = params.inputValue.toLowerCase().trim();
      const isExisting = possibleValues.some(candidate => value === candidate);
      if (value && !isExisting) {
        filtered.push({value, label: `Add "${value}"`});
      }

      return filtered;
    },
    [possibleValues],
  );

  const fullLabel = rules && rules.required ? `${label} *` : label;

  return (
    <FormGroup>
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
        }}
      >
        <select hidden name={name} multiple value={selectedValues} readOnly>
          {selectedValues.map(value => (
            <option key={value} value={value} />
          ))}
        </select>
        <FormLabel sx={{marginTop: 1}}>{fullLabel}</FormLabel>
        <Box sx={{display: 'flex', flexWrap: 'wrap', marginBottom: 1}}>
          {selectedOptions.map(option => (
            <OptionButton
              key={option.value}
              option={option}
              onClick={() => {
                update(removeIfPresent(selectedOptions, option));
              }}
            />
          ))}
        </Box>
        <Autocomplete
          autoComplete
          autoHighlight
          autoSelect
          disableClearable
          freeSolo
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          value={value}
          inputValue={value}
          onInputChange={handleInputChange}
          onChange={handleChange}
          filterOptions={filterOptions}
          options={unselectedOptions}
          getOptionLabel={getOptionLabel}
          onBlur={field.onBlur}
          renderOption={(props, option) => <li {...props}>{option.label}</li>}
          renderInput={params => <TextField {...params} label={inputLabel} />}
        />
        <FormHelperText error={name in errors}>{errorMessage}</FormHelperText>
      </Paper>
    </FormGroup>
  );
}

MultiSelect.propTypes = {
  label: string,
  name: string.isRequired,
  inputLabel: string,
  rules: object,
  possibleValues: arrayOf(string),
  defaultValues: arrayOf(string),
};

export default MultiSelect;
